import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconPlus } from '@tabler/icons-react';
import { Button, Card, LoadingOverlay, Paper } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import DataView from './components/DataView';
import { TreatmentType } from '../../../../models/core/treatments.type';
import { Filter, FilterData } from './components/Filter';
import PageContent from '../../../../components/core/PageContent/PageContent';
import treatmentsTreatment from '../../../../services/core/treatments.service';
import { Feature, SessionStorageKey } from '../../../../utils/constants.utils';
import { Permission } from '../../../../models/core/departments.type';

function TreatmentSearch() {
  const navigate = useNavigate();

  const [data, setData] = useState<TreatmentType[]>([]);
  const [loading, setLoading] = useState(false);

  /* eslint-disable @typescript-eslint/no-use-before-define */
  useEffect(() => {
    const cacheResult = JSON.parse(
      sessionStorage.getItem(SessionStorageKey.TreatmentSearch) || '[]'
    ) as TreatmentType[];

    if (cacheResult.length > 0) {
      setData(cacheResult);
    } else {
      filter({ residuoTratamento: '' });
    }
  }, []);

  const clear = () => {
    sessionStorage.removeItem(SessionStorageKey.TreatmentSearch);
    setData([]);
  };

  const filter = async (inputFilterData: FilterData) => {
    try {
      setLoading(true);
      const result = await treatmentsTreatment.search({
        residuoTratamento: inputFilterData?.residuoTratamento || null,
        limit: 1000,
        offset: 0,
      });
      sessionStorage.setItem(SessionStorageKey.TreatmentSearch, JSON.stringify(result));
      setData(result);
    } catch (error: any) {
      showNotification({
        title: 'Tratamentos',
        message: error?.isBusinessException ? error.description : 'Não foi possível pesquisar tratamentos.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmActionResult = async (item: TreatmentType, action: string, confirmed: boolean) => {
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      switch (action) {
        case 'excluir':
          await treatmentsTreatment.delete({ idResiduoTratamento: item.idResiduoTratamento });
          setData(data.filter((x) => x.idResiduoTratamento !== item.idResiduoTratamento));
          break;
        case 'visualizar':
          navigate(`/treatments/${item.idResiduoTratamento}`, {
            state: {
              treatment: item,
            },
          });
          return;
        case 'editar':
          navigate(`/treatments/${item.idResiduoTratamento}/edit`, {
            state: {
              treatment: item,
            },
          });
          return;
        default:
          break;
      }

      showNotification({
        title: 'Tratamentos',
        message: `Tratamento excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Tratamentos',
        message: error?.isBusinessException ? error.description : `Não foi possível ${action} o tratamento.`,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Treatment}
        title="Tratamentos"
        description="Gerencie tratamentos."
        buttons={[
          <Button
            key="Adicionar"
            color="primary"
            leftSection={<IconPlus size={18} />}
            disabled={loading}
            onClick={() => navigate('/treatments/add')}
            data-permission={Permission.TreatmentAdd}
          >
            Adicionar
          </Button>,
        ]}
      />
      <PageContent>
        <Filter filter={filter} clear={clear} loading={loading} />
        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading} />
          <Paper shadow="xs" p="md" withBorder>
            <DataView data={data} confirmActionResult={confirmActionResult} />
          </Paper>
        </div>
      </PageContent>
    </Card>
  );
}

export default TreatmentSearch;
